import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Home, User, Lock, Shield } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const Login: React.FC = () => {
  const { t } = useI18n();
  const { login, verify2FA, user, token, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (!authLoading && user && token && !loginSuccess) {
      // Se já estiver autenticado e não foi um login recente, redirecionar
      navigate('/dashboard', { replace: true });
    }
  }, [user, token, authLoading, loginSuccess, navigate]);

  // Redirecionar quando autenticado (após login bem-sucedido)
  useEffect(() => {
    if (!authLoading && user && token && loginSuccess) {
      // Aguardar um tick para garantir que o estado foi atualizado
      const timer = setTimeout(() => {
        navigate('/dashboard', { replace: true });
        setLoginSuccess(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, token, authLoading, loginSuccess, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    if (!authLoading && user && token) {
      navigate('/dashboard', { replace: true });
      return;
    }

    const emailValue = email.trim();
    const passwordValue = password.trim();

    if (!emailValue) {
      setError(t('login.emailRequired'));
      return;
    }

    if (!passwordValue) {
      setError(t('login.passwordRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await login(emailValue, passwordValue);

      if (result.success) {
        setLoginSuccess(true);
        if (result.requiresSetup) {
          navigate('/setup-2fa', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
        return;
      }

      if (result.requires2FA && result.userId) {
        setRequires2FA(true);
        setUserId(result.userId);
      } else {
        setError(result.error || 'Email ou senha incorretos');
      }
    } catch (error) {
      console.error('Erro no handleSubmit:', error);
      setError(t('login.connectionError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !twoFactorCode.trim()) {
      setError(t('login.twoFactorCodeRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await verify2FA(userId, twoFactorCode.trim());

      if (success) {
        // Marcar login como bem-sucedido - o useEffect vai redirecionar
        setLoginSuccess(true);
        setIsLoading(false);
      } else {
        setError(t('login.twoFactorInvalid'));
        setTwoFactorCode('');
      }
    } catch (error) {
      console.error('Erro ao verificar código 2FA:', error);
      setError(t('login.twoFactorVerifyError'));
    }

    setIsLoading(false);
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #6b21a8, #7c3aed, #a855f7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    // Mobile optimization
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '1rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '28rem',
    padding: '2rem',
    // Mobile optimization
    minHeight: 'auto',
    margin: '0 auto'
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '2rem'
  };

  const titleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem'
  };


  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  };

  const inputGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.25rem'
  };

  const inputContainerStyle: React.CSSProperties = {
    position: 'relative'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    paddingLeft: '2.5rem',
    paddingRight: '1rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const iconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    height: '1.25rem',
    width: '1.25rem',
    color: '#9ca3af'
  };

  const eyeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af'
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem'
  };

  const submitButtonStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: '#9333ea',
    color: 'white',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease-in-out'
  };

  const loadingStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const spinnerStyle: React.CSSProperties = {
    animation: 'spin 1s linear infinite',
    borderRadius: '50%',
    height: '1.25rem',
    width: '1.25rem',
    borderBottom: '2px solid white',
    marginRight: '0.5rem'
  };

  const footerStyle: React.CSSProperties = {
    marginTop: '1.5rem',
    textAlign: 'center'
  };

  const linkStyle: React.CSSProperties = {
    marginLeft: '0.25rem',
    color: '#9333ea',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none'
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={titleStyle}>
            <Home style={{ height: '3rem', width: '3rem', color: '#9333ea', marginRight: '0.75rem' }} />
            <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>{t('login.appName')}</h1>
          </div>
          <p style={{ color: '#4b5563', margin: 0 }}>{t('login.tagline')}</p>
        </div>

        {/* Login Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            {t('login.title')}
          </h2>
          <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0' }}>
            {t('login.subtitle')}
          </p>
        </div>

        {/* Form */}
        {!requires2FA ? (
          <form onSubmit={handleSubmit} style={formStyle} data-testid="login-form">
            {/* Email */}
            <div style={inputGroupStyle}>
              <label htmlFor="login-email" style={labelStyle}>{t('common.email')}</label>
              <div style={inputContainerStyle}>
                <User style={iconStyle} />
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                  placeholder={t('login.emailPlaceholder')}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div style={inputGroupStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                <label htmlFor="login-password" style={labelStyle}>{t('login.passwordLabel')}</label>
                <Link
                  to="/forgot-password"
                  style={{
                    fontSize: '0.875rem',
                    color: '#9333ea',
                    textDecoration: 'none',
                    fontWeight: '500',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseOut={(e) => (e.currentTarget.style.textDecoration = 'none')}
                >{t('login.forgotPasswordLink')}</Link>
              </div>
              <div style={inputContainerStyle}>
                <Lock style={iconStyle} />
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...inputStyle, paddingRight: '3rem' }}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={eyeButtonStyle}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff style={{ height: '1.25rem', width: '1.25rem' }} /> : <Eye style={{ height: '1.25rem', width: '1.25rem' }} />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={errorStyle} data-testid="login-error">{error}</div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit"
              style={submitButtonStyle}
              onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#7c3aed')}
              onMouseOut={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#9333ea')}
            >
              {isLoading ? (
                <div style={loadingStyle}>
                  <div style={spinnerStyle}></div>
                  Entrando...
                </div>
              ) : (
                'Entrar'
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handle2FASubmit} style={formStyle}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <Shield style={{ height: '3rem', width: '3rem', color: '#9333ea', margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', margin: '0 0 0.5rem 0' }}>{t('login.twoFactorVerification')}</h3>
              <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>{t('login.twoFactorAppHint')}</p>
            </div>

            {/* 2FA Code */}
            <div style={inputGroupStyle}>
              <label htmlFor="login-2fa-code" style={labelStyle}>{t('login.verificationCode')}</label>
              <div style={inputContainerStyle}>
                <Shield style={iconStyle} />
                <input
                  id="login-2fa-code"
                  data-testid="login-2fa-code"
                  name="twoFactorCode"
                  type="text"
                  inputMode="numeric"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={inputStyle}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  autoFocus
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={errorStyle}>{error}</div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || twoFactorCode.length !== 6}
              style={submitButtonStyle}
              onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#7c3aed')}
              onMouseOut={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#9333ea')}
            >
              {isLoading ? (
                <div style={loadingStyle}>
                  <div style={spinnerStyle}></div>
                  Verificando...
                </div>
              ) : (
                'Verificar'
              )}
            </button>

            {/* Back Button */}
            <button
              type="button"
              onClick={() => {
                setRequires2FA(false);
                setUserId(null);
                setTwoFactorCode('');
                setError('');
              }}
              style={{
                ...submitButtonStyle,
                backgroundColor: '#f3f4f6',
                color: '#111827',
                marginTop: '0.5rem',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e5e7eb')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            >{t('login.back')}</button>
          </form>
        )}

        {/* Footer */}
        <div style={footerStyle}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>{t('login.contactAdmin')}</p>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.75rem 0 0' }}>
            <Link to="/privacy" style={linkStyle}>
              {t('login.privacyPolicy')}
            </Link>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        input:focus {
          outline: 2px solid transparent;
          outline-color: #a855f7;
          border-color: transparent;
        }
        
        button:focus {
          outline: 2px solid transparent;
          outline-offset: 2px;
        }
        
        button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default Login;